'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import AppImage from '@/components/ui/AppImage';
import { Search, MapPin, Clock, Heart, X, Edit2, Trash2, PauseCircle, CheckCircle, Star, Share2, ChevronLeft, Phone, MessageCircle, Flag, Zap, Eye, Package } from 'lucide-react';

interface Product {
  id: string;
  title: string;
  priceCNY: number;
  priceKES: number;
  location: string;
  timeAgo: string;
  condition: string;
  conditionColor: string;
  category: string;
  image: string;
  alt: string;
  seller: string;
  sellerVerified: boolean;
  views: number;
  saved: number;
  status: 'active' | 'sold' | 'on_hold' | 'featured';
  description: string;
  isOwner?: boolean;
  moq?: string; // minimum order quantity
}

const CNY_RATE = 19.8; // 1 CNY ≈ 19.8 KES

const initialProducts: Product[] = [
{
  id: 'cn-001', title: 'Toyota Hilux Spare Parts Bundle — Full Set', priceCNY: 4800, priceKES: Math.round(4800 * CNY_RATE),
  location: 'Guangzhou', timeAgo: '1 hr ago', condition: 'New', conditionColor: '#00C853',
  category: 'Car Spares', seller: 'AutoParts GZ', sellerVerified: true, views: 2340, saved: 189,
  status: 'featured', isOwner: false, moq: 'MOQ: 1 set',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a2772093-1767354683202.png",
  alt: 'Toyota Hilux engine and body spare parts laid out on workshop floor',
  description: 'Complete Toyota Hilux spare parts bundle. Includes: engine gaskets, brake pads, filters (oil/air/fuel), belts, and suspension bushings. OEM quality. Fits 2015-2023 models. Shipped from Guangzhou warehouse.'
},
{
  id: 'cn-002', title: 'Industrial Sewing Machine — 3-Thread Overlock', priceCNY: 1200, priceKES: Math.round(1200 * CNY_RATE),
  location: 'Shenzhen', timeAgo: '3 hrs ago', condition: 'New', conditionColor: '#00C853',
  category: 'Machinery', seller: 'ShenTech Mfg', sellerVerified: true, views: 890, saved: 67,
  status: 'active', isOwner: false, moq: 'MOQ: 2 units',
  image: "https://images.unsplash.com/photo-1621383650959-808079fc5861",
  alt: 'Industrial overlock sewing machine on factory floor',
  description: '3-thread overlock industrial sewing machine. 7000 RPM, differential feed, adjustable stitch length. Ideal for garment factories. CE certified. Includes 1-year warranty and spare needles.'
},
{
  id: 'cn-003', title: 'Xiaomi Redmi Note 13 Pro 256GB — Bulk', priceCNY: 1580, priceKES: Math.round(1580 * CNY_RATE),
  location: 'Beijing', timeAgo: '2 hrs ago', condition: 'New', conditionColor: '#00C853',
  category: 'Electronics', seller: 'XiaoMi Direct', sellerVerified: true, views: 3200, saved: 245,
  status: 'active', isOwner: true, moq: 'MOQ: 5 units',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a1740c00-1773054280551.png",
  alt: 'Xiaomi Redmi Note 13 Pro smartphone in midnight black',
  description: 'Xiaomi Redmi Note 13 Pro 256GB/8GB RAM. 200MP camera, 5000mAh battery, 67W fast charge. Available in Midnight Black, Arctic White, Coral Purple. Bulk pricing available for 10+ units.'
},
{
  id: 'cn-004', title: 'Excavator Mini 1.5T — Kubota Engine', priceCNY: 68000, priceKES: Math.round(68000 * CNY_RATE),
  location: 'Shandong', timeAgo: '1 day ago', condition: 'New', conditionColor: '#00C853',
  category: 'Machinery', seller: 'HeavyMach CN', sellerVerified: true, views: 1560, saved: 112,
  status: 'on_hold', isOwner: true, moq: 'MOQ: 1 unit',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d8be20da-1766584860483.png",
  alt: 'Yellow mini excavator on construction site',
  description: 'Mini excavator 1.5 ton with Kubota diesel engine. Rubber tracks, 360° rotation, hydraulic thumb. Ideal for landscaping, trenching, and small construction. FOB Qingdao. Shipping arranged.'
},
{
  id: 'cn-005', title: 'Solar Panel 400W Monocrystalline — 10pcs', priceCNY: 3200, priceKES: Math.round(3200 * CNY_RATE),
  location: 'Yiwu', timeAgo: '4 hrs ago', condition: 'New', conditionColor: '#00C853',
  category: 'Solar', seller: 'SolarYiwu', sellerVerified: true, views: 2100, saved: 178,
  status: 'active', isOwner: false, moq: 'MOQ: 10 pcs',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ecb7f242-1772510969073.png",
  alt: 'Monocrystalline solar panels installed on rooftop',
  description: '400W monocrystalline solar panels. 21.5% efficiency, 25-year power warranty. Tier 1 manufacturer. IEC/CE/TUV certified. Suitable for residential and commercial installations. Container loads available.'
},
{
  id: 'cn-006', title: 'Nissan Navara Body Parts — Bonnet, Doors', priceCNY: 2800, priceKES: Math.round(2800 * CNY_RATE),
  location: 'Guangzhou', timeAgo: '6 hrs ago', condition: 'New', conditionColor: '#00C853',
  category: 'Car Spares', seller: 'AutoParts GZ', sellerVerified: true, views: 670, saved: 45,
  status: 'active', isOwner: false, moq: 'MOQ: 1 set',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18749f8f8-1784474248498.png",
  alt: 'Nissan Navara body panels including bonnet and doors in warehouse',
  description: 'Nissan Navara D40/D23 body parts. Bonnet, front doors (pair), rear doors (pair). Steel construction, primer coated. Fits 2005-2021 models. Quality checked before shipping.'
},
{
  id: 'cn-007', title: 'Corn Thresher Machine 5T/hr Diesel', priceCNY: 8500, priceKES: Math.round(8500 * CNY_RATE),
  location: 'Henan', timeAgo: '2 days ago', condition: 'New', conditionColor: '#00C853',
  category: 'Agriculture', seller: 'AgriMach HN', sellerVerified: true, views: 1890, saved: 134,
  status: 'featured', isOwner: false, moq: 'MOQ: 1 unit',
  image: "https://images.unsplash.com/photo-1524023728535-4dfbae78a42f",
  alt: 'Yellow corn thresher machine in agricultural field',
  description: 'Diesel corn thresher 5 tons/hour capacity. 15HP engine, adjustable threshing drum, grain cleaner included. Suitable for maize, sorghum, and millet. Widely used in East Africa. Spare parts available.'
},
{
  id: 'cn-008', title: 'Ceramic Floor Tiles 60x60cm — 500sqm', priceCNY: 9800, priceKES: Math.round(9800 * CNY_RATE),
  location: 'Foshan', timeAgo: '8 hrs ago', condition: 'New', conditionColor: '#00C853',
  category: 'Construction', seller: 'FoshanTiles', sellerVerified: true, views: 780, saved: 56,
  status: 'sold', isOwner: false, moq: 'MOQ: 100 sqm',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_19d88c62a-1777132759011.png",
  alt: 'Polished white ceramic floor tiles in modern showroom',
  description: 'Premium polished ceramic floor tiles 60x60cm. Marble-look finish, anti-slip grade R9. 500sqm available at bulk price. Foshan Grade A quality. CIF Mombasa pricing available on request.'
}];


const categories = ['All', 'Car Spares', 'Machinery', 'Electronics', 'Agriculture', 'Solar', 'Construction'];

const formatCNY = (n: number) => `¥${n.toLocaleString('zh-CN')}`;
const formatKES = (n: number) => `KES ${n.toLocaleString('en-KE')}`;

export default function ChinaOffersPage() {
  const [products, setProducts] = useState<Product[]>(initialProducts);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [selected, setSelected] = useState<Product | null>(null);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [currency, setCurrency] = useState<'CNY' | 'KES'>('CNY');
  const [savedItems, setSavedItems] = useState<Set<string>>(new Set());

  const filtered = products.filter((p) => {
    const matchSearch = !search || p.title.toLowerCase().includes(search.toLowerCase());
    const matchCat = activeCategory === 'All' || p.category === activeCategory;
    return matchSearch && matchCat;
  });

  const toggleSave = (id: string) => {
    setSavedItems((prev) => {const n = new Set(prev);n.has(id) ? n.delete(id) : n.add(id);return n;});
  };

  const handleDelete = (id: string) => {
    setProducts((prev) => prev.filter((p) => p.id !== id));
    setShowDeleteConfirm(null);
    if (selected?.id === id) setSelected(null);
  };

  const handleStatusChange = (id: string, status: Product['status']) => {
    setProducts((prev) => prev.map((p) => p.id === id ? { ...p, status } : p));
    if (selected?.id === id) setSelected((prev) => prev ? { ...prev, status } : null);
  };

  const openEdit = (p: Product) => {setEditProduct(p);setEditTitle(p.title);setEditPrice(String(p.priceCNY));setEditDesc(p.description);};

  const saveEdit = () => {
    if (!editProduct) return;
    const newPrice = Number(editPrice) || editProduct.priceCNY;
    setProducts((prev) => prev.map((p) => p.id === editProduct.id ? { ...p, title: editTitle, priceCNY: newPrice, priceKES: Math.round(newPrice * CNY_RATE), description: editDesc } : p));
    if (selected?.id === editProduct.id) setSelected((prev) => prev ? { ...prev, title: editTitle, priceCNY: newPrice, priceKES: Math.round(newPrice * CNY_RATE), description: editDesc } : null);
    setEditProduct(null);
  };

  const getPrice = (p: Product) => currency === 'CNY' ? formatCNY(p.priceCNY) : formatKES(p.priceKES);

  const statusBadge = (status: Product['status']) => {
    if (status === 'sold') return { label: 'SOLD', bg: '#ef4444', color: '#fff' };
    if (status === 'on_hold') return { label: 'ON HOLD', bg: '#f59e0b', color: '#000' };
    if (status === 'featured') return { label: '⭐ FEATURED', bg: '#7c3aed', color: '#fff' };
    return null;
  };

  return (
    <AppLayout>
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 pb-24">
        {/* Header */}
        <div className="sticky top-0 z-20 py-4 flex flex-col gap-3" style={{ background: 'rgba(10,15,30,0.97)', backdropFilter: 'blur(20px)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🇨🇳</span>
              <div>
                <h1 className="text-xl font-extrabold text-foreground leading-tight">China Offers</h1>
                <p className="text-[10px] font-semibold" style={{ color: 'var(--muted-foreground)' }}>Car Spares · Machinery · Electronics · Bulk</p>
              </div>
            </div>
            <button onClick={() => setCurrency((c) => c === 'CNY' ? 'KES' : 'CNY')}
            className="px-3 py-1.5 rounded-xl text-xs font-bold transition-all active:scale-95"
            style={{ background: 'rgba(0,200,83,0.12)', border: '1px solid var(--primary)', color: 'var(--primary)' }}>
              {currency === 'CNY' ? '🇨🇳 CNY' : '🇰🇪 KES'}
            </button>
          </div>
          <div className="flex items-center gap-3 px-4 py-2.5 rounded-2xl" style={{ background: 'var(--input)', border: '1px solid var(--border)' }}>
            <Search size={16} style={{ color: 'var(--muted-foreground)', flexShrink: 0 }} />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search China offers..."
            className="flex-1 bg-transparent outline-none text-sm font-medium text-foreground placeholder-muted-foreground" />
            {search && <button onClick={() => setSearch('')}><X size={14} style={{ color: 'var(--muted-foreground)' }} /></button>}
          </div>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
            {categories.map((cat) =>
            <button key={cat} onClick={() => setActiveCategory(cat)}
            className="flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all active:scale-95"
            style={{ background: activeCategory === cat ? 'var(--primary)' : 'var(--secondary)', color: activeCategory === cat ? '#000' : 'var(--muted-foreground)', border: `1px solid ${activeCategory === cat ? 'var(--primary)' : 'var(--border)'}` }}>
                {cat}
              </button>
            )}
          </div>
        </div>

        {/* Info bar */}
        <div className="flex items-center gap-3 py-3 mb-2 flex-wrap">
          <span className="text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>{filtered.length} offers</span>
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>·</span>
          <span className="text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>1 CNY ≈ KES {CNY_RATE}</span>
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>·</span>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: 'rgba(220,38,38,0.12)', color: '#f87171' }}>🇨🇳 Direct from China</span>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,200,83,0.08)', color: 'var(--primary)' }}>🚢 Shipping Available</span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {filtered.map((item) => {
            const badge = statusBadge(item.status);
            const isSaved = savedItems.has(item.id);
            return (
              <div key={item.id} className="listing-card cursor-pointer" onClick={() => setSelected(item)}>
                <div className="relative">
                  <AppImage src={item.image} alt={item.alt} width={300} height={180} className="w-full h-40 object-cover" />
                  {badge &&
                  <span className="absolute inset-0 flex items-center justify-center">
                      <span className="px-3 py-1 rounded-lg text-sm font-extrabold tracking-widest" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
                    </span>
                  }
                  <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold" style={{ background: item.conditionColor, color: '#000' }}>{item.condition}</span>
                  <button className="absolute top-2 right-2 p-1.5 rounded-full transition-all active:scale-90" style={{ background: 'rgba(0,0,0,0.5)' }}
                  onClick={(e) => {e.stopPropagation();toggleSave(item.id);}}>
                    <Heart size={13} color={isSaved ? '#ef4444' : '#fff'} fill={isSaved ? '#ef4444' : 'none'} />
                  </button>
                  {item.status === 'featured' && <span className="absolute bottom-2 left-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold" style={{ background: '#7c3aed', color: '#fff' }}>⭐ Featured</span>}
                </div>
                <div className="p-3">
                  <p className="text-xs font-semibold text-foreground truncate mb-1">{item.title}</p>
                  <p className="text-sm font-extrabold mb-0.5" style={{ color: 'var(--primary)' }}>{getPrice(item)}</p>
                  <p className="text-[10px] mb-1" style={{ color: 'var(--muted-foreground)' }}>
                    {currency === 'CNY' ? `≈ ${formatKES(item.priceKES)}` : `≈ ${formatCNY(item.priceCNY)}`}
                  </p>
                  {item.moq &&
                  <span className="inline-block px-1.5 py-0.5 rounded-full text-[9px] font-bold mb-1" style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b' }}>{item.moq}</span>
                  }
                  <div className="flex items-center gap-1 mb-1">
                    <MapPin size={10} style={{ color: 'var(--muted-foreground)' }} />
                    <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>{item.location}, China</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Clock size={10} style={{ color: 'var(--muted-foreground)' }} />
                      <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>{item.timeAgo}</span>
                    </div>
                    {item.sellerVerified && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(0,200,83,0.12)', color: 'var(--primary)' }}>✓ Verified</span>}
                  </div>
                  {item.isOwner &&
                  <div className="flex items-center gap-1 mt-2 pt-2" style={{ borderTop: '1px solid var(--border)' }}>
                      <button className="flex-1 flex items-center justify-center gap-1 py-1 rounded-lg text-[10px] font-semibold transition-all active:scale-95"
                    style={{ background: 'rgba(59,130,246,0.12)', color: '#60a5fa' }}
                    onClick={(e) => {e.stopPropagation();openEdit(item);}}><Edit2 size={10} /> Edit</button>
                      <button className="flex-1 flex items-center justify-center gap-1 py-1 rounded-lg text-[10px] font-semibold transition-all active:scale-95"
                    style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b' }}
                    onClick={(e) => {e.stopPropagation();handleStatusChange(item.id, item.status === 'on_hold' ? 'active' : 'on_hold');}}>
                        <PauseCircle size={10} /> {item.status === 'on_hold' ? 'Unhold' : 'Hold'}</button>
                      <button className="flex-1 flex items-center justify-center gap-1 py-1 rounded-lg text-[10px] font-semibold transition-all active:scale-95"
                    style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444' }}
                    onClick={(e) => {e.stopPropagation();handleStatusChange(item.id, item.status === 'sold' ? 'active' : 'sold');}}>
                        <CheckCircle size={10} /> {item.status === 'sold' ? 'Unsell' : 'Sold'}</button>
                    </div>
                  }
                </div>
              </div>);

          })}
        </div>

        {filtered.length === 0 &&
        <div className="flex flex-col items-center justify-center py-20 gap-4">
            <span className="text-5xl">🇨🇳</span>
            <p className="text-base font-semibold text-foreground">No offers found</p>
          </div>
        }
      </div>

      {/* Detail Modal */}
      {selected &&
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }} onClick={() => setSelected(null)}>
          <div className="w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[92vh] overflow-y-auto"
        style={{ background: 'var(--card)', border: '1px solid var(--border)' }} onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              <AppImage src={selected.image} alt={selected.alt} width={600} height={320} className="w-full h-56 object-cover" />
              <button className="absolute top-3 left-3 p-2 rounded-xl" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={() => setSelected(null)}>
                <ChevronLeft size={18} color="#fff" />
              </button>
              {statusBadge(selected.status) &&
            <div className="absolute inset-0 flex items-center justify-center">
                  <span className="px-4 py-2 rounded-xl text-lg font-extrabold tracking-widest"
              style={{ background: statusBadge(selected.status)!.bg, color: statusBadge(selected.status)!.color }}>
                    {statusBadge(selected.status)!.label}
                  </span>
                </div>
            }
              <span className="absolute top-3 right-3 px-2 py-1 rounded-full text-[10px] font-bold" style={{ background: selected.conditionColor, color: '#000' }}>{selected.condition}</span>
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h2 className="text-lg font-extrabold text-foreground leading-tight flex-1">{selected.title}</h2>
                <button onClick={() => toggleSave(selected.id)}>
                  <Heart size={20} color={savedItems.has(selected.id) ? '#ef4444' : 'var(--muted-foreground)'} fill={savedItems.has(selected.id) ? '#ef4444' : 'none'} />
                </button>
              </div>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-2xl font-extrabold" style={{ color: 'var(--primary)' }}>{getPrice(selected)}</span>
                <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>≈ {currency === 'CNY' ? formatKES(selected.priceKES) : formatCNY(selected.priceCNY)}</span>
              </div>
              {selected.moq &&
            <span className="inline-block px-2 py-1 rounded-full text-xs font-bold mb-3" style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b' }}>
                  <Package size={11} className="inline mr-1" />{selected.moq}
                </span>
            }
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-1"><MapPin size={12} style={{ color: 'var(--muted-foreground)' }} /><span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{selected.location}, China</span></div>
                <div className="flex items-center gap-1"><Clock size={12} style={{ color: 'var(--muted-foreground)' }} /><span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{selected.timeAgo}</span></div>
                <div className="flex items-center gap-1"><Eye size={12} style={{ color: 'var(--muted-foreground)' }} /><span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{selected.views} views</span></div>
              </div>
              <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--muted-foreground)' }}>{selected.description}</p>
              {/* Shipping info */}
              <div className="flex items-center gap-3 p-3 rounded-2xl mb-4" style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)' }}>
                <span className="text-xl">🚢</span>
                <div>
                  <p className="text-xs font-semibold text-foreground">Shipping to Kenya Available</p>
                  <p className="text-[11px]" style={{ color: 'var(--muted-foreground)' }}>FOB / CIF Mombasa · 25-35 days transit</p>
                </div>
              </div>
              {/* Seller */}
              <div className="flex items-center gap-3 p-3 rounded-2xl mb-4" style={{ background: 'var(--secondary)', border: '1px solid var(--border)' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold" style={{ background: 'rgba(220,38,38,0.15)', color: '#f87171' }}>{selected.seller[0]}</div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">{selected.seller}</p>
                  <p className="text-[11px]" style={{ color: 'var(--muted-foreground)' }}>🇨🇳 China · {selected.sellerVerified ? '✓ Verified Supplier' : 'Unverified'}</p>
                </div>
                <div className="flex items-center gap-1"><Star size={12} fill="#FFB300" color="#FFB300" /><span className="text-xs font-semibold text-foreground">4.7</span></div>
              </div>
              {selected.status !== 'sold' ?
            <div className="flex flex-col gap-2">
                  <div className="grid grid-cols-2 gap-2">
                    <button className="flex items-center justify-center gap-2 py-3 rounded-2xl font-semibold text-sm transition-all active:scale-95" style={{ background: 'var(--primary)', color: '#000' }}><Phone size={16} /> Call Supplier</button>
                    <button className="flex items-center justify-center gap-2 py-3 rounded-2xl font-semibold text-sm transition-all active:scale-95" style={{ background: '#25D366', color: '#fff' }}><MessageCircle size={16} /> WhatsApp</button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button className="flex items-center justify-center gap-2 py-2.5 rounded-2xl font-semibold text-xs transition-all active:scale-95" style={{ background: 'var(--secondary)', border: '1px solid var(--border)', color: 'var(--foreground)' }}><Share2 size={14} /> Share</button>
                    <button className="flex items-center justify-center gap-2 py-2.5 rounded-2xl font-semibold text-xs transition-all active:scale-95" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444' }}><Flag size={14} /> Report Ad</button>
                  </div>
                  {selected.isOwner &&
              <div className="mt-2 p-3 rounded-2xl" style={{ background: 'rgba(0,200,83,0.05)', border: '1px solid rgba(0,200,83,0.15)' }}>
                      <p className="text-[10px] font-bold mb-2" style={{ color: 'var(--primary)' }}>YOUR LISTING</p>
                      <div className="grid grid-cols-4 gap-1.5">
                        <button className="flex flex-col items-center gap-1 py-2 rounded-xl text-[10px] font-semibold transition-all active:scale-95" style={{ background: 'rgba(59,130,246,0.12)', color: '#60a5fa' }} onClick={() => {setSelected(null);openEdit(selected);}}><Edit2 size={14} /> Edit</button>
                        <button className="flex flex-col items-center gap-1 py-2 rounded-xl text-[10px] font-semibold transition-all active:scale-95" style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b' }} onClick={() => handleStatusChange(selected.id, selected.status === 'on_hold' ? 'active' : 'on_hold')}><PauseCircle size={14} /> {selected.status === 'on_hold' ? 'Unhold' : 'Hold'}</button>
                        <button className="flex flex-col items-center gap-1 py-2 rounded-xl text-[10px] font-semibold transition-all active:scale-95" style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444' }} onClick={() => handleStatusChange(selected.id, selected.status === 'sold' ? 'active' : 'sold')}><CheckCircle size={14} /> {selected.status === 'sold' ? 'Unsell' : 'Sold'}</button>
                        <button className="flex flex-col items-center gap-1 py-2 rounded-xl text-[10px] font-semibold transition-all active:scale-95" style={{ background: 'rgba(124,58,237,0.12)', color: '#a78bfa' }} onClick={() => handleStatusChange(selected.id, 'featured')}><Zap size={14} /> Feature</button>
                      </div>
                      <button className="w-full mt-2 flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-semibold transition-all active:scale-95" style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444' }} onClick={() => {setSelected(null);setShowDeleteConfirm(selected.id);}}><Trash2 size={13} /> Delete Listing</button>
                    </div>
              }
                </div> :

            <div className="flex items-center justify-center py-4 rounded-2xl" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                  <span className="text-sm font-bold" style={{ color: '#ef4444' }}>This item has been sold</span>
                </div>
            }
            </div>
          </div>
        </div>
      }

      {/* Edit Modal */}
      {editProduct &&
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }} onClick={() => setEditProduct(null)}>
          <div className="w-full sm:max-w-md rounded-3xl p-6" style={{ background: 'var(--card)', border: '1px solid var(--border)' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-extrabold text-foreground">Edit Listing</h3>
              <button onClick={() => setEditProduct(null)}><X size={20} style={{ color: 'var(--muted-foreground)' }} /></button>
            </div>
            <div className="flex flex-col gap-4">
              <div><label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Title</label>
                <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none text-foreground" style={{ background: 'var(--input)', border: '1px solid var(--border)' }} /></div>
              <div><label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Price (CNY ¥)</label>
                <input type="number" value={editPrice} onChange={(e) => setEditPrice(e.target.value)} className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none text-foreground" style={{ background: 'var(--input)', border: '1px solid var(--border)' }} /></div>
              <div><label className="text-xs font-semibold mb-1.5 block" style={{ color: 'var(--muted-foreground)' }}>Description</label>
                <textarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)} rows={3} className="w-full px-4 py-3 rounded-xl text-sm font-medium outline-none text-foreground resize-none" style={{ background: 'var(--input)', border: '1px solid var(--border)' }} /></div>
              <div className="flex gap-3">
                <button onClick={() => setEditProduct(null)} className="flex-1 py-3 rounded-2xl font-semibold text-sm" style={{ background: 'var(--secondary)', border: '1px solid var(--border)', color: 'var(--muted-foreground)' }}>Cancel</button>
                <button onClick={saveEdit} className="flex-1 py-3 rounded-2xl font-semibold text-sm" style={{ background: 'var(--primary)', color: '#000' }}>Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      }

      {/* Delete Confirm */}
      {showDeleteConfirm &&
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }} onClick={() => setShowDeleteConfirm(null)}>
          <div className="w-full max-w-sm rounded-3xl p-6 text-center" style={{ background: 'var(--card)', border: '1px solid var(--border)' }} onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(239,68,68,0.12)' }}><Trash2 size={24} color="#ef4444" /></div>
            <h3 className="text-lg font-extrabold text-foreground mb-2">Delete Listing?</h3>
            <p className="text-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteConfirm(null)} className="flex-1 py-3 rounded-2xl font-semibold text-sm" style={{ background: 'var(--secondary)', border: '1px solid var(--border)', color: 'var(--muted-foreground)' }}>Cancel</button>
              <button onClick={() => handleDelete(showDeleteConfirm)} className="flex-1 py-3 rounded-2xl font-semibold text-sm" style={{ background: '#ef4444', color: '#fff' }}>Delete</button>
            </div>
          </div>
        </div>
      }
    </AppLayout>);

}